// /frontend/src/pages/account/OrderDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import { 
  FaArrowLeft, 
  FaSpinner, 
  FaCreditCard, 
  FaMoneyCheck, 
  FaUniversity,
  FaPrint, 
  FaTimesCircle,
  FaTruck,
  FaCheck,
  FaClock
} from 'react-icons/fa';
import { orderService } from '../../services/orderService';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  
  // State for order data
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch order details
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/account/orders');
      return;
    }
    
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const orderData = await orderService.getOrderById(orderId);
        setOrder(orderData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.message || 'There was a problem fetching your order details.');
        setLoading(false);
      }
    };
    
    fetchOrder();
  }, [orderId, isAuthenticated, navigate]);
  
  // Format payment method for display
  const getPaymentMethodDisplay = () => {
    if (!order) return null;
    
    switch (order.order.paymentMethod) {
      case 'credit_card':
        return {
          icon: <FaCreditCard className="text-teal-800" />,
          name: 'Credit Card',
          details: 'Credit Card Payment'
        };
      case 'cheque':
        return {
          icon: <FaMoneyCheck className="text-teal-800" />,
          name: 'Cheque',
          details: 'Cheque Payment'
        };
      case 'wire_transfer':
        return {
          icon: <FaUniversity className="text-teal-800" />,
          name: 'Wire Transfer',
          details: 'Wire Transfer'
        };
      default:
        return {
          icon: <FaCreditCard className="text-teal-800" />,
          name: 'Other',
          details: 'Payment details'
        };
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Generate order timeline based on status
  const getOrderTimeline = () => {
    if (!order) return [];
    
    const timeline = [
      { 
        status: 'Order Placed', 
        date: order.order.createdAt, 
        completed: true,
        icon: <FaCheck className="text-white" />,
        description: 'Your order has been received and is being processed.'
      },
      { 
        status: 'Payment', 
        date: order.order.isPaid ? order.order.paidAt : null, 
        completed: order.order.isPaid,
        icon: order.order.isPaid ? <FaCheck className="text-white" /> : <FaClock className="text-white" />,
        description: order.order.isPaid 
          ? 'Payment has been received and confirmed.' 
          : 'Payment is being processed or awaiting confirmation.'
      },
      { 
        status: 'Processing', 
        date: order.order.status === 'processing' || order.order.status === 'shipped' || order.order.status === 'delivered' ? order.order.updatedAt : null, 
        completed: order.order.status === 'processing' || order.order.status === 'shipped' || order.order.status === 'delivered',
        icon: order.order.status === 'processing' || order.order.status === 'shipped' || order.order.status === 'delivered' 
          ? <FaCheck className="text-white" /> 
          : <FaClock className="text-white" />,
        description: 'Your order is being prepared for shipping.'
      },
      { 
        status: 'Shipped', 
        date: order.order.status === 'shipped' || order.order.status === 'delivered' ? order.order.updatedAt : null, 
        completed: order.order.status === 'shipped' || order.order.status === 'delivered',
        icon: order.order.status === 'shipped' || order.order.status === 'delivered' 
          ? <FaCheck className="text-white" /> 
          : <FaClock className="text-white" />,
        description: order.order.trackingNumber 
          ? `Your order has been shipped. Tracking number: ${order.order.trackingNumber}` 
          : 'Your order has been shipped.'
      },
      { 
        status: 'Delivered', 
        date: order.order.isDelivered ? order.order.deliveredAt : null, 
        completed: order.order.isDelivered,
        icon: order.order.isDelivered ? <FaCheck className="text-white" /> : <FaClock className="text-white" />,
        description: 'Your order has been delivered.'
      }
    ];
    
    // Handle canceled orders
    if (order.order.status === 'canceled') {
      timeline.push({
        status: 'Canceled',
        date: order.order.updatedAt,
        completed: true,
        icon: <FaTimesCircle className="text-white" />,
        description: 'This order has been canceled.'
      });
    }
    
    return timeline;
  };
  
  // Handle print order
  const handlePrintOrder = () => {
    window.print();
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <FaSpinner className="animate-spin text-teal-800 text-4xl" />
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-100 text-red-700 p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p>{error}</p>
            <div className="mt-4">
              <Link to="/account/orders" className="text-teal-800 hover:text-teal-900 font-medium">
                Return to My Orders
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!order) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p>Order not found.</p>
            <div className="mt-4">
              <Link to="/account/orders" className="text-teal-800 hover:text-teal-900 font-medium">
                Return to My Orders
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <Link to="/account/orders" className="flex items-center text-teal-800 hover:text-teal-900">
            <FaArrowLeft className="mr-2" />
            <span>Back to My Orders</span>
          </Link>
          
          <div>
            <button 
              onClick={handlePrintOrder}
              className="flex items-center px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            >
              <FaPrint className="mr-1" />
              <span>Print</span>
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          <div className="border-b border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Order #{order.order._id.substring(order.order._id.length - 8)}</h1>
                <p className="text-gray-600">Placed on {formatDate(order.order.createdAt)}</p>
              </div>
              
              <div className="mt-4 sm:mt-0">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium 
                  ${order.order.status === 'delivered' ? 'bg-green-100 text-green-800' : 
                    order.order.status === 'shipped' ? 'bg-purple-100 text-purple-800' : 
                    order.order.status === 'processing' ? 'bg-blue-100 text-blue-800' : 
                    order.order.status === 'canceled' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'}`}
                >
                  {order.order.status === 'delivered' ? <FaCheck className="mr-1" /> : 
                   order.order.status === 'shipped' ? <FaTruck className="mr-1" /> : 
                   order.order.status === 'processing' ? <FaSpinner className="mr-1" /> : 
                   order.order.status === 'canceled' ? <FaTimesCircle className="mr-1" /> : 
                   <FaClock className="mr-1" />}
                  <span className="capitalize">{order.order.status}</span>
                </span>
              </div>
            </div>
          </div>
          
          {/* Order Timeline */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Order Progress</h2>
            
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-7 top-0 h-full w-1 bg-gray-200"></div>
              
              {/* Timeline Steps */}
              <div className="space-y-6">
                {getOrderTimeline().map((step, index) => (
                  <div key={index} className="flex">
                    <div className={`relative z-10 flex items-center justify-center w-14 h-14 rounded-full border-2 ${
                      step.completed ? 'bg-teal-800 border-teal-800' : 
                      order.order.status === 'canceled' && step.status === 'Canceled' ? 'bg-red-600 border-red-600' : 
                      'bg-gray-400 border-gray-400'
                    }`}>
                      {step.icon}
                    </div>
                    
                    <div className="ml-4 flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-md font-medium text-gray-900">{step.status}</h3>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                        </div>
                        
                        {step.date && (
                          <div className="text-sm text-gray-500">
                            {formatDate(step.date)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 border-b border-gray-200">
            {/* Shipping Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-3">Shipping Information</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{order.order.shippingAddress.fullName}</p>
                <p>{order.order.shippingAddress.address}</p>
                <p>{order.order.shippingAddress.city}, {order.order.shippingAddress.state} {order.order.shippingAddress.postalCode}</p>
                <p>{order.order.shippingAddress.country}</p>
                <p className="mt-2">Phone: {order.order.shippingAddress.phone}</p>
              </div>
            </div>
            
            {/* Payment Information */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-3">Payment Information</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  {getPaymentMethodDisplay().icon}
                  <span className="ml-2 font-medium">{getPaymentMethodDisplay().name}</span>
                </div>
                
                <p>{getPaymentMethodDisplay().details}</p>
                
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="flex justify-between">
                    <span>Status:</span>
                    <span className={order.order.isPaid ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                      {order.order.isPaid ? 'Paid' : 'Pending'}
                    </span>
                  </p>
                  
                  {order.order.isPaid && order.order.paidAt && (
                    <p className="flex justify-between text-sm text-gray-600 mt-1">
                      <span>Date:</span>
                      <span>{formatDate(order.order.paidAt)}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Order Items */}
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Order Items</h2>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.order.orderItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gray-100 rounded overflow-hidden mr-3">
                            {item.image ? (
                              <img 
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/placeholder-image.jpg';
                                }}
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-gray-400">
                                No Image
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            {item.variants && Object.keys(item.variants).length > 0 && (
                              <div className="text-xs text-gray-500">
                                {Object.entries(item.variants).map(([key, value]) => (
                                  <div key={key}>{key}: {value}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        ${item.price.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Order Summary */}
            <div className="flex justify-end">
              <div className="w-full sm:w-64">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${order.order.itemsPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping:</span>
                    <span className="font-medium">
                      {order.order.shippingPrice === 0 ? 'Free' : `$${order.order.shippingPrice.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium">${order.order.taxPrice.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex justify-between font-bold">
                    <span>Total:</span>
                    <span>${order.order.totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions Section */}
        <div className="flex flex-col sm:flex-row justify-between">
          <Link
            to="/account/orders"
            className="inline-block px-6 py-3 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors mb-3 sm:mb-0 text-center"
          >
            Back to My Orders
          </Link>
          
          <Link
            to="/browse"
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors text-center"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetailPage;